<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-cog"></i
          >{{
            `${$label("venue.venue", "Venue")}${
              noDataExist ? "" : ": " + venueName
            }`
          }}
          <select
            v-model="selectedVenueId"
            class="form-control course-select pull-right"
            style="margin-right: 15px;"
          >
            <optgroup
              v-for="facility in facilities"
              :label="facility.name"
              v-bind:key="facility.id"
            >
              <option
                v-for="venue in facility.venues"
                :value="venue.id"
                v-bind:key="venue.id"
                >{{ venue.name }}</option
              >
            </optgroup>
          </select>
        </h1>
      </div>
    </div>

    <navigation-tabs :venue-id="selectedVenueId"></navigation-tabs>

    <div v-show="loading" class="row">
      <div class="col-xs-12">
        <view-loader
          margin-top-pixels="15"
          border-color="primary"
        ></view-loader>
      </div>
    </div>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <alert-box
        v-show="!loading && images.length < 1"
        type="warning"
        id="alert-no-venue-images"
        :closeable="false"
        :show-icon="false"
      >
        {{ $label("venue.no_images_exist", "No images exist.") }}
      </alert-box>

      <!-- modal dialog to upload/crop/change an image -->
      <div
        v-if="showImageModal"
        class="modal fade"
        :class="{
          in: showImageModal
        }"
        id="venueImgModal"
        tabindex="-1"
        role="dialog"
        aria-labelledby="venueImgModalLabel"
        aria-hidden="true"
        :style="{
          display: showImageModal ? 'block' : 'none',
          background: showImageModal ? '#0005' : 'transparent'
        }"
      >
        <div class="modal-dialog">
          <div class="modal-content tooltip-wrapper">
            <div id="update-img-form">
              <div class="modal-header">
                <button
                  type="button"
                  class="close"
                  :class="{ 'is-disabled': updating }"
                  @click="closeModal"
                  aria-hidden="true"
                >
                  &times;
                </button>
                <h3 class="modal-title" id="venueImgModalLabel">
                  <i class="text-primary fa fa-fw fa-lg fa-image"></i>
                  {{ $label("common.add_image", "Add image") }}
                </h3>
              </div>

              <div class="modal-body text-center">
                <alert-box
                  class="mt-4"
                  v-if="hasError('image')"
                  type="danger"
                  id="alert-upload-image-validation"
                  :closeable="false"
                  :show-icon="false"
                >
                  {{ getError("image") }}
                </alert-box>
                <alert-box
                  class="mt-4"
                  v-if="errorMessage !== null"
                  type="danger"
                  id="alert-upload-image-server-error"
                  :closeable="false"
                  :show-icon="false"
                >
                  {{ errorMessage }}
                </alert-box>
                <cropper
                  id="cropper-img"
                  classname="cropper img-responsive img-thumbnail"
                  :src="newUrl"
                  :stencilProps="{
                    maxWidth: 560,
                    maxHeight: 400,
                    handlersClassnames: handlersClassProps,
                    linesClassnames: linesClassProps
                  }"
                  @change="change"
                ></cropper>
                <div>
                  <span
                    class="btn btn-primary btn-lg btn-file"
                    :class="{ 'is-disabled': updating }"
                    v-tooltip="{
                      content: $label(
                        'common.upload_new_image',
                        'Upload new image'
                      ),
                      classes: 'custom-tooltip'
                    }"
                  >
                    <i class="fa fa-lg fa-fw fa-upload"></i>
                    <input
                      id="cropper-upload"
                      name="file"
                      type="file"
                      ref="venueImage"
                      accept="image/*"
                      @change="onImageFileChange"
                    />
                  </span>
                  <button
                    id="cropper-zoom-in"
                    class="btn btn-primary"
                    :class="{ 'is-disabled': updating }"
                    v-tooltip="{
                      content: $label('common.enlarge_image', 'Enlarge image'),
                      classes: 'custom-tooltip'
                    }"
                  >
                    <i class="fa fa-lg fa-fw fa-plus-circle"></i>
                  </button>
                  <button
                    id="cropper-zoom-out"
                    class="btn btn-primary"
                    :class="{ 'is-disabled': updating }"
                    v-tooltip="{
                      content: $label('common.reduce_image', 'Reduce image'),
                      classes: 'custom-tooltip'
                    }"
                  >
                    <i class="fa fa-lg fa-fw fa-minus-circle"></i>
                  </button>
                </div>
              </div>

              <div class="modal-footer">
                <button
                  class="btn btn-default"
                  :class="{ 'is-disabled': updating }"
                  aria-hidden="true"
                  @click="closeModal"
                >
                  {{ $label("common.cancel", "Cancel") }}
                </button>
                <button
                  class="btn btn-primary"
                  id="save-img"
                  data-dismiss="modal"
                  @click="uploadVenueImage"
                  :style="{ pointerEvents: updating ? 'none' : 'all' }"
                >
                  <view-loader
                    v-if="updating"
                    id="button-loader"
                    borderColor="white"
                  >
                  </view-loader>
                  <span v-else>
                    {{ $label("common.ok", "OK") }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="!loading" class="row image-wrapper">
        <div class="col-sm-6" v-for="(image, index) in images" :key="index">
          <h2>
            <i class="fa fa-fw fa-lg fa-picture-o"></i>
            {{ $label("venue.venue-image", "Venue image") }}{{ index + 1 }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form v-form-box">
              <fieldset class="venue-images">
                <div class="row text-center">
                  <div class="col-sm-7">
                    <div id="img_preview">
                      <img
                        :src="image.url"
                        class="img-responsive img-thumbnail col-12 col"
                      />
                    </div>
                  </div>
                  <div class="col-sm-5 center text-center">
                    <div class="text-center venue-image-actions">
                      <button
                        id="delete-venue-image"
                        class="btn btn-primary btn-block v-form-btn"
                        :class="{
                          'is-disabled': selectedVenueId == null
                        }"
                        v-tooltip="{
                          content: $label(
                            'common.delete_image',
                            'Delete image'
                          ),
                          classes: 'custom-tooltip'
                        }"
                        @click="deleteVenueImage(image.id)"
                      >
                        <i class="fa fa-lg fa-fw fa-trash-o"></i>
                        {{ $label("common.delete_image", "Delete image") }}
                      </button>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="VenueGeneralSettings"
            :class="{ 'is-disabled': updating }"
            id="back-venue-image-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="new-venue-image-button"
            icon="fa fa-lg fa-fw fa-upload"
            :text="$label('common.new_image', 'New image')"
            data-btn="upload"
            v-tooltip="{
              content: $label('common.new_image', 'New image'),
              classes: 'custom-tooltip'
            }"
            @click.native="openModal"
          >
          </info-button>
        </div>
      </div>
      <div class="row mb-4"><div class="col-xs-12">&nbsp;</div></div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";
import { Cropper } from "vue-advanced-cropper";
import FormErrorsMixin from "@/mixins/FormErrors";

import ViewLoader from "@/components/Common/ViewLoader";
import NavigationTabs from "@/components/Settings/Venue/NavigationTabs";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import BackButton from "@/components/Common/Buttons/BackButton";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import Error404 from "@/components/Common/Error404";

export default {
  name: "Images.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    AlertBox,
    BackButton,
    InfoButton,
    Cropper,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      images: [],
      facilities: [],
      selectedVenueId: null,
      selectedVenue: null,
      showImageModal: false,
      newUrl: require("@/assets/img/default-event.png"),
      newImage: null,
      croppedImage: null,
      validatedInputs: ["image"],
      errorMessage: null,
      handlersClassProps: {
        east: "custom-handler",
        eastNorth: "custom-handler",
        north: "custom-handler",
        westNorth: "custom-handler",
        west: "custom-handler",
        westSouth: "custom-handler",
        south: "custom-handler",
        eastSouth: "custom-handler"
      },
      linesClassProps: {
        east: "custom-horizontal-line",
        west: "custom-horizontal-line",
        south: "custom-vertical-line",
        north: "custom-vertical-line"
      }
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedVenueId = vm.$route.params.id;
      vm.getFacilities(() => {
        if (
          vm.selectedVenueId !== null &&
          vm.selectedVenueId !== undefined &&
          vm.selectedVenueId !== "default"
        ) {
          vm.getVenue(() => {
            vm.getImages(() => {
              //
            });
          });
        }
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getFacilities(() => {
      if (
        this.selectedVenueId !== null &&
        this.selectedVenueId !== undefined &&
        this.selectedVenueId !== "default"
      ) {
        this.getVenue(() => {
          this.getImages(() => {
            //
          });
        });
      }
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data.data;
          if (!this.selectedVenueId || this.selectedVenueId === "default") {
            const selectedFacility = this.facilities.find(
              facility => facility.venues.length > 0
            );
            if (selectedFacility) {
              this.selectedVenueId = selectedFacility.venues[0].id;
            }
          }
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    getVenue(successCallback) {
      this.loading = true;
      this.images = [];
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedVenue = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
          this.loading = false;
        });
    },

    getImages(successCallback) {
      const endpoint = this.$partnerEndpoint(
        "venues/" + this.selectedVenueId + "/images"
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.images = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    uploadVenueImage(successCallback) {
      if (this.loading || this.updating || !this.newImage) {
        return;
      }

      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "venues/" + this.selectedVenueId + "/images"
      );

      const formData = new FormData();
      formData.append("image", this.croppedImage);
      Api()
        .post(endpoint, formData, {
          headers: {
            "content-type": "multipart/form-data"
          }
        })
        .then(() => {
          this.getImages();
          swal("Success!", "The image was uploaded.", "success");
          this.closeModal();

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(e => {
          this.storeErrors(e.response.data.errors);

          if (parseInt(e.response.status / 100, 10) === 5) {
            if (e.response.status % 100 < 10) {
              this.errorMessage = "An error occurred.";
            } else {
              this.closeModal();
              swal({
                title:
                  "Something wrong happened and the image was not uploaded",
                icon: "error",
                dangerMode: true
              });
            }
          }
          console.log(e);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteVenueImage(imageId) {
      if (this.loading || this.updating) {
        return;
      }
      swal({
        title: "Are you sure?",
        text: "The image will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          this.updating = true;
          const endpoint = this.$partnerEndpoint(
            "venues/" + this.selectedVenueId + "/images/" + imageId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.getImages();
            })
            .catch(e => {
              console.log(e);
            })
            .finally(() => {
              this.updating = false;
            });
        }
      });
    },

    onImageFileChange(e) {
      const file = e.target.files[0];
      var reader = new FileReader();
      reader.readAsDataURL(file);
      this.newUrl = URL.createObjectURL(file);
      this.newImage = file;
      this.croppedImage = file;
    },

    openModal() {
      this.showImageModal = true;
    },

    closeModal() {
      this.resetErrors();
      this.showImageModal = false;
      this.newUrl = require("@/assets/img/default-event.png");
      this.newImage = null;
      this.croppedImage = null;
      this.errorMessage = null;
    },

    change({ canvas }) {
      const dataURL = canvas.toDataURL();
      const blobBin = atob(dataURL.split(",")[1]);
      const array = [];
      for (let i = 0; i < blobBin.length; i++) {
        array.push(blobBin.charCodeAt(i));
      }
      this.croppedImage = new Blob([new Uint8Array(array)], {
        type: "image/png"
      });
    }
  },

  computed: {
    venueName() {
      return this.selectedVenue ? this.selectedVenue.name : "";
    }
  },
  watch: {
    selectedVenueId(selectedVenueId) {
      if (this.$router.currentRoute.params.id === selectedVenueId) return;
      this.$router.push({
        name: "VenueImages",
        params: { id: selectedVenueId }
      });
    }
  }
};
</script>

<style scoped>
.image-wrapper {
  display: flex;
  flex-wrap: wrap;
}

.mt-4 {
  margin-top: 1.2rem;
}

.mb-4 {
  margin-bottom: 1.2rem;
}

#button-loader {
  transform: scale(0.5);
  width: 19px;
  height: 0;
  left: -9px;
  top: -19px;
}
</style>
